import React from 'react'
import { get } from 'lodash'
import ErrorMessage, { ErrorMessageProps } from 'components/ErrorMessage'
import { trackTransactionalError } from 'lib/utils/errors'
import getDisplayName from 'lib/get-display-name'

const defaultOptions = {
  path: 'error',
  title: 'Oops'
}

type Options = ErrorMessageProps & {
  path: string
}

type GetOptions<Props> = (props: Props) => Partial<Options>

interface WithErrorState {
  options: Options
  hasError: boolean
  shouldTrack: boolean
}

export default function withError<Props>(opts: GetOptions<Props> | Partial<Options> = {}) {
  return <P extends Props>(Component: React.ComponentType<P>) => {
    const componentName = getDisplayName(Component)

    return class WithError extends React.Component<P, WithErrorState> {
      static displayName = `WithError(${componentName})`

      static getDerivedStateFromProps(props, state) {
        const providedOptions = typeof opts === 'function' ? opts(props) : opts
        const options: Options = Object.assign({}, defaultOptions, providedOptions)
        const hasError = Boolean(get(props, options.path))

        // Only send track calls when the derived `hasError` state changes from `false` to `true`
        const shouldTrack = !state.hasError && hasError

        return { options, hasError, shouldTrack }
      }

      state = {
        options: defaultOptions,
        hasError: false,
        shouldTrack: false
      }

      componentDidMount() {
        this.trackPotentialError()
      }

      componentDidUpdate() {
        this.trackPotentialError()
      }

      trackPotentialError = () => {
        if (!this.state.shouldTrack) {
          return
        }

        const path = get(this.props, 'route.path', '')
        const title = get(this.state.options, 'title')

        trackTransactionalError(componentName, {
          path,
          transactionTitle: title
        })
      }

      render() {
        if (this.state.hasError) {
          return <ErrorMessage {...this.state.options} />
        }

        return <Component {...this.props} />
      }
    }
  }
}
